"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import TextCarousel from "../ui/textCarousel";
import AwardsAccordionSlider from "../slider/awardsAccordionSlider";
import BottomCurves from "../curves/bottomCurves";
import RevealElement from "../revealElement";
import BannerFloatingArrow from "../uiDecor/bannerFloatingArrow";
import { ChevronDownIcon } from "@heroicons/react/16/solid";

export default function PastAwards({ data }: any) {
    const carouselTextRef = useRef(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const yearOptions: number[] = useMemo(() => {
        const years: number[] = (data.awards || [])
            .map((award: any) => award.year)
            .filter((year: any): year is number => Number.isFinite(year));

        const unique = years.filter((y, i) => years.indexOf(y) === i);
        return unique.sort((a, b) => b - a);
    }, [data.awards]);

    const [selectedYear, setSelectedYear] = useState<number | null>(
        yearOptions.length > 0 ? yearOptions[0] : null,
    );

    const filteredAwards = useMemo(() => {
        if (selectedYear === null || !yearOptions.length) {
            return data.awards || [];
        }
        return (data.awards || []).filter(
            (award: any) => award.year === selectedYear,
        );
    }, [data.awards, selectedYear, yearOptions]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                dropdownRef.current &&
                !dropdownRef.current.contains(e.target as Node)
            ) {
                setIsDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const splitHeading = (heading: string) => {
        const splitText = heading.split(".");
        return splitText.map((text: string, index: number) => (
            <p key={index} className="mb-0">
                {text.trim()}
                {index !== splitText.length - 1 && "."}
            </p>
        ));
    };

    return (
        <section className="bg-rainbow pb-[12vw] pt-10 px-0 md:pt-[5vw]">
            <div ref={carouselTextRef} className="mb-4">
                <TextCarousel
                    text={data.headline}
                    color={"purple"}
                    colouredPosition={1}
                />
            </div>
            <RevealElement>
                <div className="text-center px-4 sm:px-[10.417vw]">
                    <div className="mb-4 md:mb-[2vw]">
                        {splitHeading(data.heading)}
                    </div>

                    {yearOptions.length > 0 && (
                        <div className="mb-4 flex justify-end md:mb-[1.5vw]">
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    type="button"
                                    className="flex items-center gap-2 rounded-full bg-[#851bed] px-5 py-2.5 text-base font-semibold text-white cursor-pointer md:gap-[0.5vw] md:px-[1.5vw] md:py-[0.75vw] md:text-[1.2vw]"
                                    onClick={() =>
                                        setIsDropdownOpen((prev) => !prev)
                                    }
                                >
                                    {selectedYear}
                                    <ChevronDownIcon
                                        className={`h-5 w-5 transition-transform duration-200 ${
                                            isDropdownOpen ? "rotate-180" : ""
                                        }`}
                                    />
                                </button>
                                {isDropdownOpen && (
                                    <div className="absolute right-0 top-full mt-2 z-20 min-w-full rounded-2xl bg-white shadow-lg overflow-hidden">
                                        {yearOptions.map((year) => (
                                            <button
                                                key={year}
                                                type="button"
                                                className={`w-full px-6 py-3 text-left text-lg font-medium transition-colors cursor-pointer ${
                                                    year === selectedYear
                                                        ? "bg-[#851bed] text-white"
                                                        : "text-[#1f2c43] hover:bg-[#f3e8ff]"
                                                }`}
                                                onClick={() => {
                                                    setSelectedYear(year);
                                                    setIsDropdownOpen(false);
                                                }}
                                            >
                                                {year}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <AwardsAccordionSlider awards={filteredAwards} />
                </div>
            </RevealElement>
            <BottomCurves direction="right" color="dark purple" />
            <BannerFloatingArrow
                direction={"left"}
                className={`left-[6vw] -top-20 md:-top-20`}
                name="awards"
            />
        </section>
    );
}
